/**
 * Single-question filter: open it for a dropdown of suggested questions, or type
 * free-form to run a backend search; pick one result to filter by. Clearing the
 * pick restores the unfiltered view.
 *
 * Distinct from QuestionPicker — that builds an ordered multi-selection for a
 * question set, with paging and a preview modal. This is a one-value *filter*
 * control.
 *
 * Search is server-side (`/question-list`) rather than over the caller's rows,
 * so it keeps working once a consumer paginates and no longer holds every row on
 * the client. `suggestions` are shown before anything is typed — the questions
 * the consumer already knows are relevant — so the common pick costs no request.
 * Because the search is global, a chosen question may have no rows in the
 * consumer's current view; consumers should say so rather than render an
 * ambiguous empty state.
 */
import { useState, useRef, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useApi } from '../api';
import LatexText from '../latex/LatexText';
import './QuestionSearchFilter.css';

export interface QuestionSearchOption {
  questionId: string;
  /** Text shown in the dropdown. */
  questionText?: string;
  /** Optional right-aligned hint, e.g. how many rows this question accounts for. */
  hint?: string;
}

export interface QuestionSearchFilterProps {
  /** Questions offered before anything is typed (no request). */
  suggestions?: QuestionSearchOption[];
  /** Heading above the suggestions list. */
  suggestionsLabel?: string;
  /** Currently filtered-to question id, or '' for no filter. */
  value: string;
  onChange: (questionId: string) => void;
  /** Text for the chip when the chosen question isn't among `suggestions` and
   *  hasn't been searched up (e.g. restored from a URL). */
  valueText?: string;
  placeholder?: string;
  /** Restrict the backend search to one subject. */
  subject?: string;
  /** Minimum query length before a request fires (default 3). */
  minChars?: number;
  /** Search teachers' own questions as well as the library (admin only —
   *  `accessCategory=all`). For surfaces over attempts, which come from both:
   *  a library-only search never finds a teacher's assignment question. */
  includeOwned?: boolean;
}

interface QuestionListItem {
  id: string;
  questionText: string;
  subject?: string;
  module: { id: string; name: string } | null;
}

const PAGE_SIZE = 50;
const DEBOUNCE_MS = 350;

export function QuestionSearchFilter({
  suggestions = [],
  suggestionsLabel = 'Suggested',
  value,
  onChange,
  valueText,
  placeholder = 'Search questions…',
  subject,
  minChars = 3,
  includeOwned = false,
}: QuestionSearchFilterProps) {
  const { fetchApi } = useApi();
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);
  // Remembered so the chip can show the text of a question picked from search
  // results, which the consumer's suggestions may not contain.
  const [pickedText, setPickedText] = useState<string | undefined>(undefined);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const searching = debounced.length >= minChars;

  const { data, isFetching } = useQuery({
    queryKey: ['question-search', debounced, subject ?? '', includeOwned],
    queryFn: () => {
      const params = new URLSearchParams({ searchString: debounced, pageSize: String(PAGE_SIZE) });
      if (subject) params.append('subject', subject);
      if (includeOwned) params.append('accessCategory', 'all');
      return fetchApi<{ questions: QuestionListItem[]; total: number }>(`/question-list?${params}`);
    },
    enabled: open && searching,
    staleTime: 60_000,
  });

  const results: QuestionSearchOption[] = useMemo(() => {
    if (!searching) return suggestions;
    return (data?.questions ?? []).map(q => ({
      questionId: q.id,
      questionText: q.questionText,
      hint: q.module?.name,
    }));
  }, [searching, suggestions, data]);

  const selectedText = useMemo(() => {
    if (!value) return undefined;
    return pickedText ?? suggestions.find(o => o.questionId === value)?.questionText ?? valueText;
  }, [value, pickedText, suggestions, valueText]);

  const pick = (o: QuestionSearchOption) => {
    setPickedText(o.questionText);
    onChange(o.questionId);
    setQuery('');
    setOpen(false);
  };

  const clear = () => {
    setPickedText(undefined);
    onChange('');
    setQuery('');
  };

  // A chosen question replaces the input with a removable chip — the filter's
  // state stays legible without the text that produced it.
  if (value) {
    return (
      <div className="qsf" ref={ref}>
        <span className="qsf-chip" title={selectedText}>
          <span className="qsf-chip-text">
            <LatexText text={selectedText ?? value} truncate={60} />
          </span>
          <button type="button" className="qsf-chip-clear" onClick={clear} aria-label="Clear question filter">
            &times;
          </button>
        </span>
      </div>
    );
  }

  const headLabel = isFetching
    ? 'Searching…'
    : searching
      ? (results.length === 0
        ? 'No matching questions'
        : `${data?.total ?? results.length} match${(data?.total ?? results.length) === 1 ? '' : 'es'}${(data?.total ?? 0) > results.length ? ` — showing ${results.length}` : ''}`)
      : (results.length === 0
        ? `Type at least ${minChars} characters to search`
        : suggestionsLabel);

  return (
    <div className="qsf" ref={ref}>
      <input
        type="text"
        className="qsf-input"
        placeholder={placeholder}
        value={query}
        onChange={e => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={e => {
          if (e.key === 'Escape') setOpen(false);
          if (e.key === 'Enter' && results.length === 1) pick(results[0]);
        }}
        aria-label="Search questions"
      />
      <span className="qsf-caret" aria-hidden="true">▾</span>
      {open && (
        <div className="qsf-popup" role="listbox">
          <div className="qsf-popup-head">{headLabel}</div>
          {results.map(o => (
            <button
              key={o.questionId}
              type="button"
              className="qsf-result"
              role="option"
              onClick={() => pick(o)}
            >
              <span className="qsf-result-text">
                <LatexText text={o.questionText ?? o.questionId} truncate={140} />
              </span>
              {o.hint && <span className="qsf-result-hint">{o.hint}</span>}
            </button>
          ))}
          {!searching && query.length > 0 && (
            <div className="qsf-popup-foot">Keep typing to search all questions…</div>
          )}
        </div>
      )}
    </div>
  );
}
